import {
  ChangeDetectionStrategy,
  Component,
  computed,
  HostBinding,
  HostListener,
  inject,
} from '@angular/core';

import { BookMeetingService } from '../../../core/services/book-meeting.service';
import { MeetingComposerService } from '../../../core/services/meeting-composer.service';
import { ThemeService } from '../../../core/services/theme.service';
import {
  SCHEDULE_PREVIEW_DATA_OVERRIDE,
  SchedulePreviewDataService,
} from '../data/schedule-preview-data.service';
import { ScheduleRealDataService } from '../data/schedule-real-data.service';
import { scheduleHostTheme } from '../styles/schedule-host-theme';

/**
 * im-meeting-composer-host — the app shell's mount of the meeting EDITOR
 * (`sp-meeting-expanded`), so every surface that creates or edits a
 * meeting opens the SAME card the schedule uses.
 *
 * ARCHITECTURE
 *   - Mounted EXACTLY ONCE in app-shell, the same invariant the Book
 *     Meeting Panel holds (and that `book-meeting-panel.architecture.spec.ts`
 *     enforces).
 *   - Consumes `MeetingComposerService.request` — any component calls
 *     `composer.open(prefill)`.
 *   - Provides its own data seam so the editor works off root services
 *     with no schedule-page boot. See `MeetingComposerHostComponent`'s
 *     seam note below: this is knowingly a SECOND instance for now.
 *   - Re-emits the `--sp-*` token layer on itself (see the SCSS).
 *
 * STATUS — slice 1 of 5. This is the vessel: the service, the layer, the
 * seam and the theme are wired and proven, and the editor state has NOT
 * moved yet. `sp-meeting-expanded` needs ~40 inputs that today are
 * computed inside `schedule-preview.component.ts`; moving them is
 * indoor_mapping_poc-e4sb.2, and until it lands this host renders nothing
 * and `open()` is a no-op you can see in the DOM but not use.
 *
 *   Deliberately shipped empty rather than half-bound: a composer wired
 *   to a subset of its inputs is a second, worse copy of the card, which
 *   is the exact failure `schedule-preview.component.html:284` records.
 *
 * SEAM — KNOWN ISSUE (indoor_mapping_poc-e4sb.5)
 *   Providing `ScheduleRealDataService` here creates a second instance
 *   alongside the schedule page's. A host that only reads could accept
 *   that. This host WRITES, and the echo-claim logic in
 *   `schedule-real-data.service.ts` (`markSelfOriginated` + `reloadDays`)
 *   is per-instance — a booking made here may not settle the schedule
 *   page's grid. That is tracked and must be resolved before the map's
 *   Add details ships.
 */
@Component({
  selector: 'im-meeting-composer-host',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    ScheduleRealDataService,
    {
      provide: SchedulePreviewDataService,
      useFactory: (): SchedulePreviewDataService =>
        inject(SCHEDULE_PREVIEW_DATA_OVERRIDE, { optional: true }) ??
        inject(ScheduleRealDataService),
    },
  ],
  template: ``,
  styleUrl: './meeting-composer-host.component.scss',
})
export class MeetingComposerHostComponent {
  protected readonly svc = inject(MeetingComposerService);
  private readonly bookMeeting = inject(BookMeetingService);
  private readonly theme = inject(ThemeService);

  /** Sized + interactive only while a composer is open. */
  @HostBinding('class.open')
  protected get isOpen(): boolean {
    return this.svc.visible();
  }

  /** The host attributes for the viewer's paper, exactly as the schedule
   *  page binds them (see `styles/schedule-host-theme.ts`). */
  private readonly hostTheme = computed(() =>
    scheduleHostTheme(this.theme.theme(), this.theme.isDark()),
  );

  /** The legacy sheet hook — light papers and Black only. */
  @HostBinding('attr.data-theme')
  protected get dataTheme(): string | null {
    return this.hostTheme().dataTheme;
  }

  /** Dark on a paper the composer follows: the schedule's own semantics. */
  @HostBinding('attr.data-sp-theme')
  protected get dataSpTheme(): string | null {
    return this.hostTheme().dataSpTheme;
  }

  /**
   * Escape closes — unless the Book Meeting Panel is layered above.
   *
   * The deferral the old detail drawer made too. It matters in both
   * directions during the migration: the BMP is still reachable from
   * routes this has not replaced yet, and an Escape must close the top
   * surface, not the one underneath it.
   */
  @HostListener('document:keydown.escape')
  protected onEscape(): void {
    if (!this.isOpen || this.bookMeeting.visible()) return;
    this.svc.close();
  }
}
